import { motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ANIMATION_DURATION } from "@/constants/animations.constants";
import { useTranslation } from "@/core/i18n";
import { MobileViewportContainer } from "@/modules/onboarding/components/MobileViewportContainer/MobileViewportContainer";
import { NotificationCard } from "@/modules/onboarding/components/NotificationCard/NotificationCard";
import { OnboardingBackButton } from "@/modules/onboarding/components/OnboardingBackButton/OnboardingBackButton";
import { storage } from "@/modules/storage";
import { useUser } from "@/modules/user";
import { Button } from "atoms/button/Button";
import { TextButton } from "atoms/text-button/TextButton";
import { usePlatform } from "hooks/usePlatform";
import { AndroidPushNotificationAdapter } from "modules/pushNotifications/AndroidPushNotificationAdapter/AndroidPushNotificationAdapter";
import { IOSPushNotificationAdapter } from "modules/pushNotifications/IOSPushNotificationAdapter/IOSPushNotificationAdapter";
import { PushNotificationService } from "modules/pushNotifications/PushNotificationService/PushNotificationService";
import { isIosPlatform, isNativeMobileApp } from "utils/platform.helpers";

export default function V3NotificationsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isRequesting, setIsRequesting] = useState(false);

  const { platform } = usePlatform();
  const { user } = useUser();
  const firstName = user?.firstName || "";

  const handleNext = useCallback(() => {
    if (platform?.platformSettings?.patientPersonalisationEnabled) {
      storage.setItem("personalisation_from_onboarding", "true");
      navigate("/personalise");
    } else {
      navigate("/home");
    }
  }, [navigate, platform?.platformSettings?.patientPersonalisationEnabled]);

  const handleEnableNotifications = async () => {
    if (!isNativeMobileApp()) {
      handleNext();
      return;
    }

    setIsRequesting(true);
    try {
      const adapter = isIosPlatform()
        ? new IOSPushNotificationAdapter()
        : new AndroidPushNotificationAdapter();
      const pushService = new PushNotificationService(adapter);
      const permissionsGranted = await pushService.requestPermission();

      if (permissionsGranted) {
        await pushService.register();
      }
    } catch (error) {
      console.error("Failed to request notification permissions:", error);
    } finally {
      setIsRequesting(false);
      handleNext();
    }
  };

  useEffect(() => {
    if (!isNativeMobileApp()) {
      handleNext();
    }
  }, [handleNext]);

  return (
    <MobileViewportContainer header={<OnboardingBackButton />}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: ANIMATION_DURATION, delay: 0.1 }}
        className="flex w-full max-w-sm flex-col items-center gap-9"
      >
        {/* Header - 36px gap after */}
        <div className="flex w-full shrink-0 flex-col gap-4">
          <p className="w-full shrink-0 font-serif text-[30px] font-normal italic leading-[36px] text-nearly-black">
            {t("v3NotificationsPage.title")}
          </p>
          <p
            className="w-full shrink-0 font-sans text-body2 text-secondary"
            style={{ fontVariationSettings: "'opsz' 14" }}
          >
            {t("v3NotificationsPage.subtitle", { platformName: platform?.name })}
          </p>
        </div>

        {/* Notification Card - 36px gap after */}
        <div className="w-full shrink-0">
          <NotificationCard
            spaName={platform?.name || ""}
            timeAgo={t("v3NotificationsPage.previewTimeAgo")}
            avatar={platform?.mobileAppIconUrlSmall || ""}
            title={t("v3NotificationsPage.previewTitle", { firstName })}
            message={t("v3NotificationsPage.previewMessage")}
            className="!max-w-full bg-white/40 shadow-[0px_3px_4px_0px_rgba(0,0,0,0.1)]"
          />
        </div>

        {/* Buttons - 16px gap between them */}
        <div className="flex w-full shrink-0 flex-col items-center gap-4">
          <Button fullWidth onClick={handleEnableNotifications} disabled={isRequesting}>
            {isRequesting ? t("v3NotificationsPage.requesting") : t("v3NotificationsPage.enable")}
          </Button>
          <TextButton className="px-3 font-normal" onClick={handleNext}>
            {t("v3NotificationsPage.later")}
          </TextButton>
        </div>
      </motion.div>
    </MobileViewportContainer>
  );
}
